import React, { useState, useEffect } from 'react';
import { RefreshCw, AlertCircle, TrendingUp, Trophy, Target } from 'lucide-react';
import PredictionCard from '../components/PredictionCard';
import ErrorBoundary from '../components/ErrorBoundary';
import { getSupabaseClient } from '../lib/supabase';
import { getNextDrawDate, generatePrediction, subscribeToUpdates } from '../lib/predictions';
import type { PredictionResult } from '../types/lottery';

interface BestPrediction {
  drawDate: string;
  predictedNumbers: number[];
  actualNumbers: number[];
  matchingCount: number;
  predictedPowerball: number;
  actualPowerball: number;
  powerballMatch: boolean;
}

export default function Home() {
  const [prediction, setPrediction] = useState<PredictionResult | null>(null);
  const [nextDraw, setNextDraw] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bestPrediction, setBestPrediction] = useState<BestPrediction | null>(null);
  const [lastDrawPrediction, setLastDrawPrediction] = useState<BestPrediction | null>(null);

  const regeneratePrediction = async () => {
    setLoading(true);
    setError(null);
    setPrediction(null); // Clear existing prediction
    await loadPrediction();
  };

  useEffect(() => {
    loadPrediction();
    fetchPredictionPerformance();

    // Subscribe to real-time updates
    const cleanup = subscribeToUpdates(
      async () => {
        console.log('Prediction update received');
        loadPrediction();
        fetchPredictionPerformance();
      },
      async () => {
        console.log('Draw update received');
        fetchPredictionPerformance();
      }
    );

    return () => {
      if (typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, []);

  async function fetchPredictionPerformance() {
    const supabase = getSupabaseClient();
    if (!supabase) return;

    // Get best prediction
    const { data: bestPredData } = await supabase
      .from('prediction_accuracy')
      .select(`
        matching_numbers,
        matching_powerball,
        predictions(numbers, powerball, draw_date),
        lottery_draws(numbers, powerball, draw_date)
      `)
      .order('matching_numbers', { ascending: false })
      .limit(1);

    if (bestPredData && bestPredData.length > 0) {
      const data = bestPredData[0];
      setBestPrediction({
        drawDate: data.predictions.draw_date,
        predictedNumbers: data.predictions.numbers,
        actualNumbers: data.lottery_draws.numbers,
        matchingCount: data.matching_numbers,
        predictedPowerball: data.predictions.powerball,
        actualPowerball: data.lottery_draws.powerball,
        powerballMatch: data.matching_powerball
      });
    }

    // Get last draw prediction
    const { data: lastDrawData } = await supabase
      .from('prediction_accuracy')
      .select(`
        matching_numbers,
        matching_powerball,
        predictions!inner(numbers, powerball),
        lottery_draws!inner(numbers, powerball, draw_date)
      `)
      .order('lottery_draws(draw_date)', { ascending: false })
      .limit(1);

    if (lastDrawData && lastDrawData.length > 0) {
      const data = lastDrawData[0];
      setLastDrawPrediction({
        drawDate: data.predictions.draw_date,
        predictedNumbers: data.predictions.numbers,
        actualNumbers: data.lottery_draws.numbers,
        matchingCount: data.matching_numbers || 0,
        predictedPowerball: data.predictions.powerball,
        actualPowerball: data.lottery_draws.powerball,
        powerballMatch: data.matching_powerball || false
      });
    }
  }

  async function loadPrediction() {
    const supabase = getSupabaseClient();
    if (!supabase) {
      setError('Database connection not available');
      setLoading(false);
      return;
    }

    try {
      const drawDate = await getNextDrawDate();
      if (!drawDate) {
        setError('Unable to fetch next draw date');
        setLoading(false);
        return;
      }

      setNextDraw(drawDate);

      // Always generate a new prediction
      const newPrediction = await generatePrediction(drawDate);
      setPrediction({
        numbers: newPrediction.numbers,
        powerball: newPrediction.powerball,
        confidence: newPrediction.confidence,
        basedOn: 'real-time analysis',
        timestamp: new Date().toLocaleDateString()
      });
      setError(null);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error loading prediction');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-4 sm:space-y-8">
      <section>
        <ErrorBoundary>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-gray-600">Loading prediction...</p>
            </div>
          ) : prediction ? (
            <>
              <PredictionCard prediction={prediction} onRegenerate={regeneratePrediction} />
              {nextDraw && (
                <p className="text-center mt-4 text-gray-600">
                  Next Draw: {nextDraw.toLocaleDateString()} at {nextDraw.toLocaleTimeString()}
                </p>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              {error ? (
                <div className="bg-red-50 border border-red-100 rounded-lg p-4 inline-flex items-center text-red-700">
                  <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              ) : (
                <div className="text-gray-600">No prediction available</div>
              )}
            </div>
          )}
        </ErrorBoundary>
      </section>
      
      <section className="bg-white rounded-xl p-8 shadow-lg border border-gray-100 mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">Prediction Performance</h2>
        <ErrorBoundary>
          <div className="grid md:grid-cols-2 gap-8">
          {bestPrediction && (
            <div className="bg-gradient-to-br from-amber-50 to-white rounded-xl p-6 border border-amber-100">
              <div className="flex items-center space-x-3 mb-4">
                <Trophy className="w-6 h-6 text-amber-600" />
                <h3 className="text-lg font-semibold text-gray-900">Best Prediction</h3>
              </div>
              <div className="space-y-3">
                <p className="text-gray-600">Draw Date: {new Date(bestPrediction.drawDate).toLocaleDateString()}</p>
                <div>
                  <p className="font-medium text-gray-700 mb-2">Predicted Numbers:</p>
                  <div className="flex flex-wrap gap-2">
                    {bestPrediction.predictedNumbers.map((num) => (
                      <span key={num} className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        bestPrediction.actualNumbers.includes(num) 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {num}
                      </span>
                    ))}
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      bestPrediction.powerballMatch
                        ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {bestPrediction.predictedPowerball}
                    </span>
                  </div>
                </div>
                <p className="text-green-600 font-medium">
                  Matched {bestPrediction.matchingCount} numbers
                  {bestPrediction.powerballMatch ? ' + Powerball!' : ''}
                </p>
              </div>
            </div>
          )}
          
          {lastDrawPrediction && (
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 border border-blue-100">
              <div className="flex items-center space-x-3 mb-4">
                <Target className="w-6 h-6 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Last Draw Results</h3>
              </div>
              <div className="space-y-3">
                <p className="text-gray-600">Draw Date: {new Date(lastDrawPrediction.drawDate).toLocaleDateString()}</p>
                <div>
                  <p className="font-medium text-gray-700 mb-2">Our Prediction:</p>
                  <div className="flex flex-wrap gap-2">
                    {lastDrawPrediction.predictedNumbers.map((num) => (
                      <span key={num} className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        lastDrawPrediction.actualNumbers.includes(num)
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {num}
                      </span>
                    ))}
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      lastDrawPrediction.powerballMatch
                        ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {lastDrawPrediction.predictedPowerball}
                    </span>
                  </div>
                </div>
                <p className="text-blue-600 font-medium">
                  Matched {lastDrawPrediction.matchingCount} numbers
                  {lastDrawPrediction.powerballMatch ? ' + Powerball!' : ''}
                </p>
              </div>
            </div>
          )}
        </div>
        </ErrorBoundary>
      </section>
      
      <section>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-semibold text-amber-800 mb-2 sm:mb-3">Important Disclaimer</h2>
          <div className="space-y-3 sm:space-y-4 text-amber-700 text-sm sm:text-base">
            <p>
              This is simply a game and you should not use these predictions to go and purchase Powerball tickets. All these numbers represent is just some stupid AI algorithm trying to predict 5+1 numbers based on some historic data, that's all it is. Obviously human intelligence is superior and we have decided that it would be illegal to predict winning Powerball numbers. In no way shape or form does this site intend to break the law. For full disclosure this paragraph has been "Spell Checked" because the author continues to misspell the word intelligence.
            </p>
            <p>
              This application is for entertainment purposes only. Lottery numbers are completely random, 
              and no AI or algorithm can predict future lottery results with any accuracy.
            </p>
            <p>
              We strongly advise against using these predictions as a basis for purchasing lottery tickets. 
              Gambling can be addictive and lead to significant financial losses.
            </p>
            <p className="font-medium">
              Remember: The lottery is a game of pure chance. Never spend money you cannot afford to lose.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}